import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import Honeybadger from '@honeybadger-io/js';
import { isNil, omit } from 'lodash';
import { Select } from 'lp-components';
import { useEffect, useState, Fragment, useRef, } from 'react';
import { Field, Form } from 'react-final-form';
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3';
import { useDispatch, useSelector } from 'react-redux';
import { Button, ButtonV2, H2, H3, InputCheckbox, InputNumber, InputPattern, InputSSN, InputText, Label, Li, P, Spinner, DOCTOR_SPECIALTIES, STATES, formatPhoneNumber, formatSSN, reportError, formatDateFromISOtoUSdateString, validateRequiredField, validDateOfBirth, composeValidators, validateEmail, validateLength, parseAmount, parseDateOfBirth, validateCurrency, } from 'shared';
import { Pencil } from 'images/svg-components';
import { toastError } from 'utils';
import { setPersonalInfo } from '../credit_application/actions';
import { EditPrefillModal, FormAbandonmentTracker, PrequalificationTermsFields, } from '../credit_application/components';
import { selectors } from '../credit_application/reducer';
const isElementExistAndHasValue = (object, elementName) => {
    if (isNil(object)) {
        return false;
    }
    return (Object.prototype.hasOwnProperty.call(object, elementName) &&
        !isNil(object[elementName]) &&
        String(object[elementName]).trim() !== '');
};
function PrefilledPersonalInfoForm({ data = null, isProvePrefilled, handleSubmit, practice, isProveLoading, fieldsToExclude, showAdditionalTerms, isSubmittingForm, setIsSubmittingForm, prefillSource, proveUpdatesVariant, applicationId, }) {
    const { variant: proveAbVariant } = proveUpdatesVariant;
    const isProveABTreatment = proveAbVariant.name === 'treatment';
    const createPrefilledInfo = (values) => {
        if (values) {
            return {
                fullName: `${values.firstName} ${values.lastName}`,
                address: `${values.street1}${values.street2 ? ` ${values.street2}` : ''}, ${values.city}, ${values.state}, ${values.zip}`,
                dateOfBirth: formatDateFromISOtoUSdateString(values.dateOfBirth),
                ...(isProveABTreatment ? {} : { ssn: values.ssn ?? '' }),
                primaryPhone: data && isElementExistAndHasValue(data, 'primaryPhone')
                    ? data.primaryPhone ?? ''
                    : '',
                email: data?.email ?? '',
            };
        }
        return {
            fullName: data &&
                isElementExistAndHasValue(data, 'firstName') &&
                isElementExistAndHasValue(data, 'lastName')
                ? `${data.firstName} ${data.lastName}`
                : '',
            address: data &&
                isElementExistAndHasValue(data, 'street1') &&
                isElementExistAndHasValue(data, 'city') &&
                isElementExistAndHasValue(data, 'state') &&
                isElementExistAndHasValue(data, 'zip')
                ? `${data.street1}${isElementExistAndHasValue(data, 'street2')
                    ? ` ${data.street2}`
                    : ''}, ${data.city}, ${data.state}, ${data.zip}`
                : '',
            dateOfBirth: data && isElementExistAndHasValue(data, 'dateOfBirth')
                ? formatDateFromISOtoUSdateString(data.dateOfBirth)
                : '',
            ...(isProveABTreatment ? {} : { ssn: data?.ssn ?? '' }),
            primaryPhone: data && isElementExistAndHasValue(data, 'primaryPhone')
                ? data.primaryPhone ?? ''
                : '',
            email: data?.email ?? '',
        };
    };
    const { executeRecaptcha } = useGoogleReCaptcha();
    const proveFail = useSelector(selectors.proveFail);
    const [initialFormCaptchaToken, setInitialFormCaptchaToken] = useState('');
    const [refreshedCaptchaToken, setRefreshedCaptchaToken] = useState('');
    const [formSubmitted, setFormSubmitted] = useState(0);
    const [tokenLoading, setTokenLoading] = useState(false);
    const [editMode, setEditMode] = useState(false);
    const [showTip, setShowTip] = useState({
        mobile: false,
        income: false,
        purchase: false,
    });
    const [formValues, setFormValues] = useState(null);
    const [prefillableFields, setPrefillableFields] = useState(createPrefilledInfo());
    const [resetDisplaySSN, setResetDisplaySSN] = useState(false);
    const [hasCompletedForm, setHasCompletedForm] = useState(false);
    const dispatch = useDispatch();
    const tokenRef = useRef('');
    const handleSubmitWithCaptcha = () => {
        setFormSubmitted((prevSubmitted) => prevSubmitted + 1);
    };
    const handleEdit = () => {
        setEditMode(true);
    };
    const handleShowTip = (tip) => {
        setShowTip({ ...showTip, [tip]: true });
    };
    const prefilledValuesLabelMapping = {
        fullName: 'Full Name',
        address: 'Address',
        dateOfBirth: 'Date of Birth',
        ...(isProveABTreatment
            ? {}
            : { ssn: isProvePrefilled ? 'SSN#' : 'SSN/ITIN' }),
        primaryPhone: 'Phone Number',
        email: 'Email',
    };
    const isPrefilledValueAvailable = (object) => {
        if (isNil(object)) {
            return false;
        }
        return Object.values(object).some((value) => value !== '');
    };
    const handleSubmitEditInformation = (values, form) => {
        setEditMode(false);
        const updatedData = { ...data, ...values };
        dispatch(setPersonalInfo(updatedData));
        setPrefillableFields(createPrefilledInfo(updatedData));
        form.change('confirmPrefilledInfo', true);
    };
    // Helper function to check if all required terms are agreed to
    const areAllTermsAgreed = (values) => {
        const requiredTerms = showAdditionalTerms
            ? ['agreedAlphaeon', 'agreedComenity', 'agreedAdvantage']
            : ['agreedAlphaeon', 'agreedComenity'];
        return requiredTerms.every((termKey) => values[termKey] === true);
    };
    const requestInitialFormCaptcha = async (action) => {
        if (executeRecaptcha) {
            try {
                const recaptchaToken = await executeRecaptcha(action);
                setInitialFormCaptchaToken(recaptchaToken);
            }
            catch (e) {
                reportError('recaptchaError', {
                    componentName: 'requestInitialFormCaptcha',
                    errType: 'form',
                    errURL: window.location.pathname,
                    errMsg: e,
                }, e);
            }
        }
    };
    const refreshReCaptchaToken = async (action) => {
        setTokenLoading(true);
        if (executeRecaptcha) {
            try {
                const refreshedReCaptchaToken = await executeRecaptcha(action);
                setRefreshedCaptchaToken(refreshedReCaptchaToken);
                if (!refreshedReCaptchaToken) {
                    toastError('Captcha Token is missing');
                    setIsSubmittingForm(false);
                }
            }
            catch (e) {
                reportError('recaptchaError', {
                    componentName: 'refreshReCaptchaToken',
                    errType: 'form',
                    errURL: window.location.pathname,
                    errMsg: e,
                }, e);
            }
        }
        setTokenLoading(false);
    };
    const formatTableFieldRow = (key, value) => {
        if (key === 'primaryPhone') {
            return formatPhoneNumber(value);
        }
        if (key === 'ssn') {
            return formatSSN(value);
        }
        return value;
    };
    const parseValues = (values) => {
        const onlyNumbers = (phoneNumber) => {
            return phoneNumber.replace(/[^\d]/g, '');
        };
        const parsedDateOfBirth = parseDateOfBirth(values.dateOfBirth);
        const parsedAlternatePhone = values.alternatePhone
            ? onlyNumbers(values.alternatePhone)
            : null;
        //when no primary phone is provided, use alternate phone as the primary phone
        const parsedPrimaryPhone = values.primaryPhone
            ? onlyNumbers(values.primaryPhone)
            : parsedAlternatePhone;
        const parsedPurchaseAmount = values.purchaseAmount
            ? parseAmount(values.purchaseAmount)
            : 10000;
        const parsedAnnualIncome = parseAmount(values.annualIncome);
        const parsedSsn = isProvePrefilled && !isProveABTreatment
            ? values.ssn
            : onlyNumbers(values.ssn);
        return {
            ...omit(values, 'confirm-prefilled-info'),
            recaptchaToken: tokenRef.current,
            dateOfBirth: parsedDateOfBirth,
            primaryPhone: parsedPrimaryPhone ?? '',
            annualIncome: parsedAnnualIncome,
            ssn: parsedSsn,
            purchaseAmount: parsedPurchaseAmount,
            ...(parsedAlternatePhone ? { alternatePhone: parsedAlternatePhone } : {}),
        };
    };
    const validateSubmission = (values) => {
        Honeybadger.setContext({
            phoneNumber: values.primaryPhone,
        });
        const errors = {
            annualIncome: '',
            dateOfBirth: '',
            zip: '',
        };
        if (!validDateOfBirth(new Date(values.dateOfBirth))) {
            errors.dateOfBirth =
                'Date of birth year must be after 1900 and applicant must be at least 18 years old';
        }
        if (values.zip.length !== 5) {
            toastError('zip is invalid');
            errors.zip = 'Must be 5 digits';
        }
        if (errors.dateOfBirth || errors.zip) {
            return errors;
        }
        return null;
    };
    const beforeSubmit = (values) => {
        setIsSubmittingForm(true);
        const errors = validateSubmission(values);
        if (errors) {
            toastError('Please review the information provided and correct all form errors');
            setIsSubmittingForm(false);
            return errors;
        }
        handleSubmitWithCaptcha();
    };
    useEffect(() => {
        const finalValidationsAndSubmit = () => {
            if (!executeRecaptcha || !formValues) {
                setIsSubmittingForm(false);
                return;
            }
            tokenRef.current = initialFormCaptchaToken;
            requestInitialFormCaptcha('personal_form_load');
            if (formSubmitted && !refreshedCaptchaToken && !tokenLoading) {
                refreshReCaptchaToken('personal_form_submit');
            }
            else if (formSubmitted > 0 && refreshedCaptchaToken) {
                tokenRef.current = refreshedCaptchaToken;
                const valuesToSubmit = parseValues(formValues);
                setHasCompletedForm(true);
                handleSubmit(valuesToSubmit);
            }
        };
        finalValidationsAndSubmit();
    }, [executeRecaptcha, refreshedCaptchaToken, formSubmitted]);
    useEffect(() => {
        if (!isProveLoading && data) {
            setPrefillableFields(createPrefilledInfo());
        }
        else if (!isProveLoading && !data) {
            setPrefillableFields(null);
            setResetDisplaySSN(true);
        }
    }, [isProvePrefilled, isProveLoading]);
    useEffect(() => {
        if (prefillSource === 'OTA' && isProveABTreatment) {
            setResetDisplaySSN(true);
        }
    }, [prefillSource]);
    return (_jsx("div", { className: "forms-container", children: _jsx(Form, { onSubmit: beforeSubmit, render: ({ handleSubmit, form, values, hasValidationErrors }) => {
                return (_jsxs("form", { onSubmit: handleSubmit, noValidate: true, children: [_jsx(UpdateFormValues, { editMode: editMode, form: form, data: data, prefillableFields: prefillableFields, isSubmittingForm: isSubmittingForm }), _jsx(FormAbandonmentTracker, { isComplete: hasCompletedForm || isSubmittingForm, prefillSource: prefillSource, applicationId: applicationId }), _jsx(EditPrefillModal, { initialValues: {
                                firstName: values.firstName,
                                lastName: values.lastName,
                                street1: values.street1,
                                street2: values.street2,
                                city: values.city,
                                state: values.state,
                                zip: values.zip,
                                dateOfBirth: values.dateOfBirth,
                            }, isOpen: editMode && isProveABTreatment, setIsOpen: setEditMode, onSubmit: (values) => handleSubmitEditInformation(values, form) }), prefillableFields &&
                            isPrefilledValueAvailable(prefillableFields) &&
                            !editMode &&
                            (prefillSource === 'Prove' || !isProveABTreatment) &&
                            (!proveFail || prefillSource === 'OTA') && (_jsxs("div", { className: "prefilled-group", children: [_jsxs("div", { className: "prefilled-group-content", children: [_jsx(H2, { children: "Make Sure Everything Is Correct" }), Object.entries(prefillableFields).map(([key, value]) => value &&
                                            key !== 'primaryPhone' && (_jsx(Fragment, { children: _jsxs("div", { className: "prefilled-field", children: [_jsx(H3, { variant: "title-large", bold: true, className: "prefilled-label", children: prefilledValuesLabelMapping[key] }), _jsx(P, { variant: "title-large", className: "prefilled-value", children: formatTableFieldRow(key, String(value)) })] }) }, key)))] }), _jsxs("div", { className: "flex align-center justify-between mar-t-16 mar-b-16 confirm-or-edit-info", children: [_jsxs("div", { className: "flex align-center", children: [_jsx(Field, { name: "confirmPrefilledInfo", dataTestId: "confirm-prefilled-info", component: InputCheckbox, validate: validateRequiredField, hideBreakTag: true, type: "checkbox" }), _jsx(Label, { htmlFor: "confirm-prefilled-info", size: "medium", className: "mar-b-0 mar-left-8", children: "I confirm this information is correct and accurate." })] }), _jsx(ButtonV2, { variant: "outline", size: "medium", SvgIcon: Pencil, onClick: handleEdit, children: "Edit Information" })] })] })), isProveLoading && (_jsxs("div", { className: "text-center", children: [_jsx(Spinner, {}), _jsx("br", {}), _jsx(P, { children: "Loading pre-filled data" })] })), (!isElementExistAndHasValue(prefillableFields, 'fullName') ||
                            editMode ||
                            (prefillSource === 'OTA' && isProveABTreatment)) && (_jsxs(_Fragment, { children: [_jsx(Field, { name: "firstName", component: InputText, placeholder: "First Name", label: "First Name", required: true, validate: validateRequiredField }), _jsx(Field, { name: "lastName", component: InputText, label: "Last Name", placeholder: "Last Name", required: true, validate: validateRequiredField })] })), (!isElementExistAndHasValue(prefillableFields, 'address') ||
                            editMode) && (_jsxs(_Fragment, { children: [_jsx(Field, { name: "street1", component: InputText, label: "Street 1", placeholder: "Address Line 1", required: true, validate: validateRequiredField }), _jsx(Field, { name: "street2", component: InputText, label: "Street 2", placeholder: "Apt, Unit, Suite, etc.(optional)" }), _jsx(Field, { name: "city", component: InputText, label: "City", placeholder: "City", required: true, validate: validateRequiredField }), _jsxs("div", { className: "one-line-text", children: [_jsx(Field, { label: 'State', name: "state", component: Select, placeholder: "State", "data-testid": "input-state", options: STATES, required: true, requiredIndicator: " *", validate: validateRequiredField }), _jsx(Field, { name: "zip", label: "Zip", type: "tel", component: InputNumber, placeholder: "Zip", required: true, validate: validateRequiredField, isAllowed: (values) => {
                                                const { value } = values;
                                                return value.length <= 5;
                                            } })] })] })), (!isElementExistAndHasValue(prefillableFields, 'dateOfBirth') ||
                            editMode ||
                            (prefillSource === 'OTA' && isProveABTreatment)) && (_jsx("div", { className: "date-birth", children: _jsx(Field, { type: "tel", component: InputPattern, label: "Date of Birth", name: "dateOfBirth", placeholder: "MM/DD/YYYY", formattype: '##/##/####', required: true, validate: validateRequiredField }) })), isProveABTreatment ? (_jsx(Field, { component: InputSSN, name: "ssn", required: true, validate: composeValidators(validateRequiredField, validateLength(9, 9, 'Must be 9 digits')), formChange: form.change, resetDisplaySSN: resetDisplaySSN, setResetDisplaySSN: setResetDisplaySSN })) : (_jsx(PreFilledInfoFormFieldSSN, { editMode: editMode, prefillableFields: prefillableFields, formChange: form.change, resetDisplaySSN: resetDisplaySSN, setResetDisplaySSN: setResetDisplaySSN })), (!isElementExistAndHasValue(prefillableFields, 'email') ||
                            editMode) && (_jsx(_Fragment, { children: _jsx(Field, { name: "email", component: InputText, label: "Email Address", placeholder: "Email Address", type: "email", required: true, validate: composeValidators(validateRequiredField, validateEmail) }) })), _jsx(Field, { type: "tel", name: "annualIncome", required: true, label: "Annual Income", component: InputNumber, preicon: "$", allowLeadingZeros: false, thousandsGroupStyle: "thousand", thousandSeparator: ",", decimalScale: 2, validate: composeValidators(validateRequiredField, validateCurrency({
                                min: {
                                    amount: 0,
                                    message: `Income must be greater than $${0}`,
                                },
                                max: {
                                    amount: 10000000,
                                    message: 'Invalid income',
                                },
                            })), isAllowed: (values) => {
                                const { value } = values;
                                return value.length < 20;
                            }, placeholder: "Annual Income", onFocus: () => {
                                if (!showTip.income)
                                    handleShowTip('income');
                            } }), showTip.income && (_jsxs("div", { className: "field-note", children: [_jsx(P, { children: "If you do not wish to have alimony, child support and/or separate maintenance income considered as a basis for repaying this obligation, please do not include it in your Annual Income amount." }), values.state === 'WI' && (_jsxs(P, { children: [_jsx("strong", { children: "Married WI Residents Only:" }), " if you are applying for an individual account and your spouse is also a WI resident, combine you and your spouse's financial information."] }))] })), !fieldsToExclude?.includes('purchaseAmount') && (_jsx(Field, { type: "tel", name: "purchaseAmount", label: "Estimated Cost of Treatment", component: InputNumber, placeholder: "Estimated Cost of Treatment (Optional)", preicon: "$", allowLeadingZeros: false, thousandsGroupStyle: "thousand", thousandSeparator: ",", decimalScale: 2, onFocus: () => {
                                if (!showTip.purchase)
                                    handleShowTip('purchase');
                            } })), showTip.purchase && (_jsx("div", { className: "field-note", children: _jsx(P, { children: "\u00A0While the Estimated Cost of Treatment is requested, please note that some providers do not take the Estimated Cost of Treatment into account for their decision." }) })), (isNil(practice) || isNil(practice.locationId)) && (_jsx(Field, { label: 'Specialty', name: "specialty", required: true, "data-testid": "input-specialty", requiredIndicator: " *", component: Select, placeholder: "Select Your Doctor's Specialty", options: DOCTOR_SPECIALTIES, validate: validateRequiredField })), _jsxs(_Fragment, { children: [!isElementExistAndHasValue(prefillableFields, 'primaryPhone') ? (_jsx(Field, { type: "tel", name: "primaryPhone", placeholder: "Mobile Number", label: "Mobile Number", disabled: prefillableFields?.primaryPhone, validate: !values.homeNumberCheck
                                        ? validateRequiredField
                                        : undefined, component: InputPattern, required: !values.homeNumberCheck, allowEmptyFormatting: false, formattype: '(###)-###-####', onFocus: () => {
                                        if (!showTip.mobile)
                                            handleShowTip('mobile');
                                    } })) : (_jsx("div", { className: "w100", children: _jsxs("div", { className: "fieldset-readonly", children: [_jsx(Label, { size: "medium", htmlFor: "disabled-primary-phone", children: "Mobile Number" }), _jsx("fieldset", { children: _jsx("input", { disabled: true, className: "disabled-phone", name: "disabled-primary-phone", type: "text", value: formatPhoneNumber(prefillableFields?.primaryPhone) }) })] }) })), (showTip.mobile || prefillableFields?.primaryPhone) && (_jsx("div", { className: "field-note", children: _jsxs("ul", { children: [_jsxs(Li, { variant: "body-large", listStyle: "disc", children: ["Please confirm that this mobile number is ready and accessible as it may be used for authentication.", ' '] }), _jsx(Li, { variant: "body-large", listStyle: "disc", children: "By proceeding you may receive a one-time security link to your mobile number. Message and data rates apply. You authorize your wireless carrier to use or disclose information about your wireless account and your wireless device, if available, to Comenity Capital Bank or its service provider for the duration of your business relationship, solely to help them identify you and your wireless device and to prevent fraud." }), _jsx(Li, { variant: "body-large", listStyle: "disc", children: "Alphaeon and their service providers may contact you at any number you provide, including by autodialer, recorded or artificial voice, and text. For further details, refer to our Consent to Autodialed Calls/Texts below." })] }) })), _jsxs("div", { className: "home-number", children: [values.homeNumberCheck && (_jsx(Field, { type: "tel", name: "alternatePhone", placeholder: "Home Number", label: "Home Number", component: InputPattern, allowEmptyFormatting: false, formattype: '(###)-###-####', required: true, validate: validateRequiredField })), _jsxs("div", { className: "input-container", children: [_jsx(Field, { name: "homeNumberCheck", type: "checkbox", placeholder: "Number", component: InputCheckbox, hideBreakTag: true }), _jsx(Label, { htmlFor: "homeNumberCheck", size: "medium", children: "I want to provide an alternate phone number (Optional)" })] })] })] }), _jsx("div", { className: "mar-b-20", children: _jsx(PrequalificationTermsFields, { showAdditionalTerms: showAdditionalTerms }) }), _jsx(Button, { type: "submit", disabled: !areAllTermsAgreed(values) ||
                                isSubmittingForm ||
                                isProveLoading, submitting: isSubmittingForm, className: `button-warn ${!areAllTermsAgreed(values) ? `warn-disabled` : ``}`, onClick: () => {
                                if (hasValidationErrors) {
                                    toastError('Please review the information provided and correct all form errors');
                                }
                                setFormValues(values);
                            }, children: "AGREE AND CONTINUE PRE-QUALIFICATION" })] }));
            } }) }));
}
function UpdateFormValues({ form, data, editMode, prefillableFields, isSubmittingForm, }) {
    useEffect(() => {
        function updateForm() {
            if (!prefillableFields && !isSubmittingForm) {
                form.initialize({});
                return;
            }
            if (!isSubmittingForm) {
                const newData = {};
                prefillableFields &&
                    Object.entries(prefillableFields).forEach(([key, value]) => {
                        if (key === 'address' && data) {
                            newData.street1 = data.street1;
                            newData.street2 = data.street2;
                            newData.city = data.city;
                            newData.state = data.state;
                            newData.zip = data.zip;
                        }
                        if (key === 'fullName' && data) {
                            newData.firstName = data.firstName;
                            newData.lastName = data.lastName;
                        }
                        if (key !== 'fullName' && key !== 'address') {
                            Object.assign(newData, { [key]: value });
                        }
                    });
                if (editMode && data) {
                    newData.dateOfBirth = formatDateFromISOtoUSdateString(data.dateOfBirth);
                }
                form.initialize(newData);
            }
        }
        requestAnimationFrame(() => {
            updateForm();
        });
    }, [editMode, data, prefillableFields]);
    return null;
}
export default PrefilledPersonalInfoForm;
export const PreFilledInfoFormFieldSSN = ({ editMode, prefillableFields, formChange, resetDisplaySSN, setResetDisplaySSN, }) => {
    return (_jsxs(_Fragment, { children: [isElementExistAndHasValue(prefillableFields, 'ssn') &&
                editMode &&
                prefillableFields &&
                'ssn' in prefillableFields && (_jsx("div", { className: "w50", children: _jsxs("div", { className: "fieldset-readonly", children: [_jsx(H3, { variant: "title-medium", children: "Social Security Number (Cannot be edited)" }), _jsx(P, { variant: "body-medium", children: formatSSN(prefillableFields?.ssn) })] }) })), !isElementExistAndHasValue(prefillableFields, 'ssn') && (_jsx(_Fragment, { children: _jsx(Field, { component: InputSSN, name: "ssn", required: true, validate: composeValidators(validateRequiredField, validateLength(9, 9, 'Must be 9 digits')), formChange: formChange, resetDisplaySSN: resetDisplaySSN, setResetDisplaySSN: setResetDisplaySSN }) }))] }));
};
